import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo, comics } from '@/mocks/comics';

function AboutSEO() {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressCountry: 'CN',
    },
    foundingDate: siteInfo.foundingYear,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫漫画网是否需要付费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫漫画网所有漫画内容完全免费，无需注册即可畅读。我们致力于为漫画爱好者提供零门槛的高清韩国漫画阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫漫画网的漫画多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们每天都会更新最新漫画章节，确保您能第一时间阅读到最新的韩国漫画内容。热门漫画通常每周更新2-3话。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在手机上阅读韩漫漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫漫画网采用响应式设计，完美适配手机、平板和电脑。您只需在手机浏览器中打开我们的网站，即可享受流畅的下拉式阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫漫画网支持哪些漫画类型？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们涵盖恋爱、动作、奇幻、悬疑、搞笑、剧情、校园、都市等多种类型的韩国漫画，满足不同读者的阅读偏好。',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

export default function About() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const faqs = [
    { q: '韩漫漫画网是否需要付费？', a: '韩漫漫画网所有漫画内容完全免费，无需注册即可畅读。我们致力于为漫画爱好者提供零门槛的高清韩国漫画阅读体验。' },
    { q: '韩漫漫画网的漫画多久更新一次？', a: '我们每天都会更新最新漫画章节，确保您能第一时间阅读到最新的韩国漫画内容。热门漫画通常每周更新2-3话。' },
    { q: '如何在手机上阅读韩漫漫画？', a: '韩漫漫画网采用响应式设计，完美适配手机、平板和电脑。您只需在手机浏览器中打开我们的网站，即可享受流畅的下拉式阅读体验，无需下载任何APP。' },
    { q: '韩漫漫画网支持哪些漫画类型？', a: '我们涵盖恋爱、动作、奇幻、悬疑、搞笑、剧情、校园、都市等多种类型的韩国漫画，满足不同读者的阅读偏好。' },
    { q: '阅读时遇到加载问题怎么办？', a: '建议您检查网络连接状态，或尝试刷新页面。如问题持续存在，可以通过下方的联系方式与我们取得联系，我们会尽快为您解决。' },
  ];

  const testimonials = [
    { name: '漫画爱好者小王', text: '韩漫漫画网的画质真的太好了！而且完全免费，更新的速度也很快，已经推荐给身边所有喜欢看韩漫的朋友了。', rating: 5 },
    { name: '读者小李', text: '下拉式阅读体验非常流畅，界面也很简洁干净，没有烦人的广告打扰，是迄今为止用过最好的漫画阅读网站。', rating: 5 },
    { name: '资深漫迷张先生', text: '收录的漫画种类很全，从热门到冷门都有。最喜欢的是排行榜功能，帮我发现了不少好作品，每天都在追更新。', rating: 5 },
    { name: '上班族陈女士', text: '通勤路上用手机看韩漫非常方便，加载速度很快，不费流量。现在已经养成了每天午休看两话的习惯。', rating: 5 },
  ];

  const brandFeatures = [
    { icon: 'ri-book-read-line', title: '海量资源', desc: `收录超过 ${comics.length * 50}+ 部高清韩国漫画，涵盖八大热门分类，每日持续更新中` },
    { icon: 'ri-image-line', title: '高清画质', desc: '所有漫画均采用高清分辨率呈现，画质清晰细腻，为您带来影院级的视觉享受' },
    { icon: 'ri-timer-flash-line', title: '极速加载', desc: '全球CDN加速节点覆盖，确保漫画页面秒速加载，告别漫长的等待时间' },
    { icon: 'ri-heart-pulse-line', title: '每日更新', desc: '热门漫画保持高频更新，让您第一时间追到最新章节，不错过任何精彩内容' },
  ];

  return (
    <>
      <ScrollToTop />
      <AboutSEO />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-6 mb-14">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 text-center">
              关于韩漫漫画网
            </h1>
            <p className="text-sm text-foreground-500 text-center mt-3 max-w-[600px] mx-auto leading-relaxed">
              {siteInfo.tagline}
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {brandFeatures.map((feature, index) => (
                <div key={index} className="p-6 rounded-xl bg-background-100/70 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-4">
                    <i className={`${feature.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <div className="bg-background-100/70 rounded-2xl p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-6">
                品牌故事
              </h2>
              <div className="max-w-[800px] mx-auto">
                <p className="text-foreground-600 leading-relaxed mb-4">
                  韩漫漫画网创立于{siteInfo.foundingYear}年，是国内最早专注于韩国漫画在线阅读的平台之一。我们深知漫画爱好者对高品质阅读体验的追求，因此从创立之初就坚持"高清画质、完全免费、流畅体验"三大核心理念。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  经过多年的发展，韩漫漫画网已经积累了海量的高清韩国漫画资源，涵盖恋爱、动作、奇幻、悬疑、搞笑、剧情、校园、都市等丰富类型，每日保持高频更新，为数百万漫画爱好者提供了优质的在线阅读服务。
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  我们的团队由一群热爱漫画的年轻人组成，我们相信好的内容应该被更多人看到。未来，韩漫漫画网将继续致力于为漫画爱好者打造更加优质的阅读平台，让更多人享受漫画带来的快乐与感动。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {testimonials.map((item, index) => (
                <div key={index} className="p-6 rounded-xl bg-background-100/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{item.text}&rdquo;
                  </p>
                  <p className="text-sm font-semibold text-foreground-900">
                    — {item.name}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-8">
              常见问题
            </h2>
            <div className="max-w-[800px] mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-background-100/70 overflow-hidden"
                >
                  <button
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-semibold text-foreground-900">
                      {faq.q}
                    </span>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`text-lg text-foreground-500 transition-transform duration-200 ${
                          activeFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                        }`}
                      ></i>
                    </div>
                  </button>
                  {activeFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <div className="bg-background-100/70 rounded-2xl p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-8">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[900px] mx-auto">
                <div className="text-center p-6">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-4">
                    <i className="ri-mail-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                  <p className="text-sm text-foreground-500">{siteInfo.email}</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-4">
                    <i className="ri-phone-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">客服电话</h3>
                  <p className="text-sm text-foreground-500">{siteInfo.phone}</p>
                </div>
                <div className="text-center p-6">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-4">
                    <i className="ri-map-pin-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">办公地址</h3>
                  <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}